import { PlusIcon } from "@heroicons/react/24/outline";
import Button from "../../../componentLibrary/Button";
import Container from "../../../componentLibrary/Container";
import { Permission, Role } from "../../../types/rbac";
import { APIError } from "../../../utils/ErrorHandling";
import RoleDetails from "./RoleDetails";
import RoleFormModal from "./RoleFormModal";
import RoleList from "./RoleList";

interface RoleFormData {
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
}

interface RolePermissionManagementViewProps {
  // Roles data
  roles: Role[];
  selectedRole: Role | null;
  search: string;
  isLoadingRoles: boolean;
  rolesError: APIError | null;

  // Permissions data
  allPermissions: Permission[];
  groupedPermissions: Record<string, Permission[]>;
  isLoadingPermissions: boolean;

  // Modal state
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  formData: RoleFormData;

  // Mutation states
  isCreating: boolean;
  isUpdating: boolean;

  // Handlers
  onSearchChange: (value: string) => void;
  onRoleSelect: (role: Role) => void;
  onOpenCreateModal: () => void;
  onOpenEditModal: (role: Role) => void;
  onDeleteRole: (role: Role) => void;
  onCloseCreateModal: () => void;
  onCloseEditModal: () => void;
  onCreateRole: () => void;
  onUpdateRole: () => void;
  onFormChange: (data: Partial<RoleFormData>) => void;
  onTogglePermission: (permissionId: string) => void;
}

const RolePermissionManagementView = ({
  roles,
  selectedRole,
  search,
  isLoadingRoles,
  rolesError,
  allPermissions,
  groupedPermissions,
  isLoadingPermissions,
  isCreateModalOpen,
  isEditModalOpen,
  formData,
  isCreating,
  isUpdating,
  onSearchChange,
  onRoleSelect,
  onOpenCreateModal,
  onOpenEditModal,
  onDeleteRole,
  onCloseCreateModal,
  onCloseEditModal,
  onCreateRole,
  onUpdateRole,
  onFormChange,
  onTogglePermission,
}: RolePermissionManagementViewProps) => {
  return (
    <div className="p-2 sm:p-4">
      <Container>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold">Role & Permission Management</h1>
            <p className="text-gray-600 mt-1">
              Create and manage roles with specific permissions
            </p>
          </div>
          <Button onClick={onOpenCreateModal}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Role
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5">
            <RoleList
              roles={roles}
              selectedRole={selectedRole}
              search={search}
              isLoading={isLoadingRoles}
              error={rolesError}
              onSearchChange={onSearchChange}
              onRoleSelect={onRoleSelect}
              onEditRole={onOpenEditModal}
              onDeleteRole={onDeleteRole}
            />
          </div>

          <div className="lg:col-span-7">
            <RoleDetails role={selectedRole} allPermissions={allPermissions} />
          </div>
        </div>
      </Container>

      <RoleFormModal
        isOpen={isCreateModalOpen}
        mode="create"
        formData={formData}
        groupedPermissions={groupedPermissions}
        isLoading={isLoadingPermissions}
        isSubmitting={isCreating}
        onClose={onCloseCreateModal}
        onSubmit={onCreateRole}
        onFormChange={onFormChange}
        onTogglePermission={onTogglePermission}
      />

      <RoleFormModal
        isOpen={isEditModalOpen}
        mode="edit"
        formData={formData}
        groupedPermissions={groupedPermissions}
        isLoading={isLoadingPermissions}
        isSubmitting={isUpdating}
        onClose={onCloseEditModal}
        onSubmit={onUpdateRole}
        onFormChange={onFormChange}
        onTogglePermission={onTogglePermission}
      />
    </div>
  );
};

export default RolePermissionManagementView;
